import React, { useCallback } from 'react';
import { useNavigation } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';

import {
  BodyText,
  Button,
  HStack,
  Modal,
  ModalFloatingCloseBtn,
  TextLink,
  theme,
  useResponsive,
  VStack,
} from '@sg/garnish';

import { useHeaderButtonOptions } from '../../../../components/Header';
import { TransparentModal } from '../../../../components/TransparentModal';
import { useCart } from '../../../../hooks';
import { useReorderRightIcon } from '../ReorderConfirmLocation/ReorderConfirmLocation.components';
import { ConflictCard } from './ReorderConflictReview.card';

import type { PartialOrderConflict } from '../../../../graphql/partials';

const cannotReorderTypes = new Set([
  'RestaurantMaxDeliveryQuantityExceeded',
  'RestaurantMaxQuantityExceeded',
  'CanNotReorder',
]);

export const ReorderConflictReviewContent = ({
  conflicts,
  reorderType,
  currentStep,
  restaurantName,
  deliveryAddressName,
  isLoading,
  shouldShowClearWarning,
  finishReorder,
  changeLocation,
  cancelReorder,
}: ReorderConflictContentProps) => {
  const { isCartEmpty } = useCart();
  const { currentBreakpoint } = useResponsive();
  const DynamicConflictContent = currentBreakpoint.isXS
    ? ConflictContentMobile
    : ConflictContentDesktop;

  if (currentStep !== 'conflictReview' && Platform.OS === 'web') return null;
  const cannotReorder = cannotReorderTypes.has(reorderType ?? '');

  return (
    <DynamicConflictContent
      cannotReorder={cannotReorder}
      conflictCount={conflicts.length}
      restaurantName={restaurantName}
      deliveryAddressName={deliveryAddressName}
      showClearWarning={shouldShowClearWarning ? !isCartEmpty : undefined}
      isLoading={isLoading}
      cancelReorder={cancelReorder}
      changeLocation={changeLocation}
      finishReorder={finishReorder}
    >
      {conflicts.map((conflict) => (
        <ConflictCard key={conflict.product.id} conflict={conflict} />
      ))}
    </DynamicConflictContent>
  );
};

const ConflictContentDesktop = ({
  cannotReorder,
  restaurantName,
  conflictCount,
  deliveryAddressName,
  showClearWarning,
  isLoading,
  children,
  cancelReorder,
  finishReorder,
  changeLocation,
}: ConflictContentProps & Readonly<{ conflictCount: number }>) => {
  const { formatMessage } = useIntl();

  return (
    <TransparentModal
      accessibilityLabel={formatMessage(messages.reorderConflictsHeadline)}
      fitHeight
      size={conflictCount > 1 ? 'large' : 'small'}
      mobileEdges={['bottom']}
      onClose={cancelReorder}
    >
      <View style={styles.bannerWrapper}>
        <ModalFloatingCloseBtn onPress={cancelReorder} style={styles.closeButton} />
        <ScrollView style={[styles.bodyDesktop, conflictCount > 1 && styles.bodyDesktopMultiple]}>
          <Modal.Headline>{formatMessage(messages.reorderConflictsHeadline)}</Modal.Headline>
          <Modal.Row>
            <BodyText sizeMatch={['14', '16']} style={styles.bodyText}>
              {deliveryAddressName
                ? formatMessage(messages.reorderConflictsReorderingTo)
                : formatMessage(messages.reorderConflictsReorderingFrom)}{' '}
              <TextLink style={styles.textUnderline} onPress={changeLocation}>
                {deliveryAddressName ? deliveryAddressName : restaurantName}
              </TextLink>
            </BodyText>
          </Modal.Row>
          <Modal.BodyText>{formatMessage(messages.reorderConflictsNotice)}</Modal.BodyText>
          <Modal.Row>
            <HStack style={styles.conflictItemsDesktop} itemsPerRow={conflictCount > 1 ? 2 : 1}>
              {children}
            </HStack>
          </Modal.Row>
        </ScrollView>
      </View>
      <Modal.Footer style={styles.footer}>
        {cannotReorder ? (
          <Button
            size="large"
            onPress={changeLocation}
            testID="reorder.change-locations"
            accessibilityLabel={formatMessage(messages.reorderChangeLocation)}
            accessibilityHint={formatMessage(messages.reorderChangeLocation)}
            accessibilityRole="button"
            style={styles.singleButtonDesktop}
          >
            {formatMessage(messages.reorderChangeLocation)}
          </Button>
        ) : (
          <HStack itemsPerRow={2} gap={theme.spacing['4']}>
            <Button
              size="large"
              palette="secondary"
              onPress={changeLocation}
              testID="reorder.change-locations"
              accessibilityLabel={formatMessage(messages.reorderChangeLocation)}
              accessibilityHint={formatMessage(messages.reorderChangeLocation)}
              accessibilityRole="button"
            >
              {formatMessage(messages.reorderChangeLocation)}
            </Button>
            <Button
              size="large"
              onPress={finishReorder}
              isLoading={isLoading}
              testID="reorder.continue-to-bag"
              accessibilityLabel={formatMessage(messages.reorderContinue)}
              accessibilityHint={formatMessage(messages.reorderContinue)}
              accessibilityRole="button"
            >
              {formatMessage(messages.reorderContinue)}
            </Button>
          </HStack>
        )}

        {showClearWarning ? (
          <View style={styles.clearWarning}>
            <BodyText sizeMatch={['12']}>{formatMessage(messages.reorderClearWarning)}</BodyText>
          </View>
        ) : null}
      </Modal.Footer>
    </TransparentModal>
  );
};

const ConflictContentMobile = ({
  cannotReorder,
  restaurantName,
  deliveryAddressName,
  showClearWarning,
  isLoading,
  children,
  cancelReorder,
  finishReorder,
  changeLocation,
}: ConflictContentProps) => {
  const { formatMessage } = useIntl();
  const rightIcon = useReorderRightIcon();
  const navigation = useNavigation();

  const renderHeaderRight = useCallback(() => rightIcon, [rightIcon]);
  const { headerRightOptions } = useHeaderButtonOptions({ headerRight: renderHeaderRight });

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <MobileHeader
          restaurantName={restaurantName}
          deliveryAddressName={deliveryAddressName}
          changeLocation={changeLocation}
        />
      ),
      ...headerRightOptions,
      headerShown: true,
    });
  }, [changeLocation, deliveryAddressName, headerRightOptions, navigation, restaurantName]);

  return (
    <TransparentModal
      testID="reorder-conflict-review"
      accessibilityLabel={formatMessage(messages.reorderConflictsHeadline)}
      size="full"
      onClose={cancelReorder}
      mobileEdges={['bottom']}
    >
      <View style={styles.bannerWrapper}>
        <ScrollView style={styles.bodyMobile}>
          <Modal.Headline>{formatMessage(messages.reorderConflictsHeadline)}</Modal.Headline>
          <Modal.BodyText>{formatMessage(messages.reorderConflictsNotice)}</Modal.BodyText>
          <Modal.Row>
            <VStack style={styles.conflictItemsMobile}>{children}</VStack>
          </Modal.Row>
        </ScrollView>
      </View>

      <Modal.Footer style={[styles.footer, styles.footerMobile]}>
        {cannotReorder ? (
          <Button
            size="large"
            onPress={changeLocation}
            accessibilityLabel={formatMessage(messages.reorderChangeLocation)}
            accessibilityHint={formatMessage(messages.reorderChangeLocation)}
            accessibilityRole="button"
          >
            {formatMessage(messages.reorderChangeLocation)}
          </Button>
        ) : (
          <HStack itemsPerRow={2} gap={theme.spacing['4']}>
            <Button
              size="large"
              palette="secondary"
              onPress={changeLocation}
              accessibilityLabel={formatMessage(messages.reorderChangeLocation)}
              accessibilityHint={formatMessage(messages.reorderChangeLocation)}
              accessibilityRole="button"
            >
              {formatMessage(messages.reorderChangeLocation)}
            </Button>
            <Button
              size="large"
              onPress={finishReorder}
              isLoading={isLoading}
              accessibilityLabel={formatMessage(messages.reorderContinueToBag)}
              accessibilityHint={formatMessage(messages.reorderContinueToBag)}
              accessibilityRole="button"
            >
              {formatMessage(messages.reorderContinueToBag)}
            </Button>
          </HStack>
        )}

        {showClearWarning ? (
          <View style={styles.clearWarning}>
            <BodyText sizeMatch={['12']}>{formatMessage(messages.reorderClearWarning)}</BodyText>
          </View>
        ) : null}
      </Modal.Footer>
    </TransparentModal>
  );
};

const MobileHeader = ({
  restaurantName,
  deliveryAddressName,
  changeLocation,
}: Pick<ConflictContentProps, 'restaurantName' | 'deliveryAddressName' | 'changeLocation'>) => {
  const { formatMessage } = useIntl();

  return (
    <View style={styles.modalHeader}>
      <BodyText sizeMatch={['14']}>
        {deliveryAddressName
          ? formatMessage(messages.reorderConflictsReorderingTo)
          : formatMessage(messages.reorderConflictsReorderingFrom)}
      </BodyText>
      <TextLink style={styles.textUnderline} onPress={changeLocation}>
        {deliveryAddressName ? deliveryAddressName : restaurantName}
      </TextLink>
    </View>
  );
};

type ConflictContentProps = Readonly<{
  restaurantName: string;
  deliveryAddressName?: string;
  cannotReorder: boolean;
  showClearWarning?: boolean;
  isLoading?: boolean;
  children?: React.ReactNode;
  finishReorder: () => void;
  changeLocation: () => void;
  cancelReorder: () => void;
}>;

type ReorderConflictContentProps = Readonly<{
  restaurantName: string;
  deliveryAddressName?: string;
  reorderType?: string;
  conflicts: readonly PartialOrderConflict[];
  currentStep: string;
  isLoading?: boolean;
  shouldShowClearWarning?: boolean;
  finishReorder: () => void;
  changeLocation: () => void;
  cancelReorder: () => void;
}>;

const styles = StyleSheet.create({
  footer: {
    paddingTop: theme.spacing['6'],
    borderTopColor: theme.colors.GREEN_1,
  },
  footerMobile: {
    paddingTop: theme.spacing['4'],
  },
  clearWarning: {
    width: '100%',
    paddingTop: theme.spacing['4'],
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalHeader: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  bodyText: {
    paddingBottom: theme.spacing['4'],
  },
  textUnderline: {
    color: theme.colors.KALE,
    textDecorationLine: 'underline',
    textDecorationColor: theme.colors.KALE,
  },
  bannerWrapper: {
    position: 'relative',
    zIndex: theme.zIndex.fixed,
    flex: 1,
  },
  bodyDesktop: {
    paddingTop: theme.spacing['6'],
    maxHeight: '100%',
  },
  bodyDesktopMultiple: {
    height: 504,
  },
  bodyMobile: {
    flex: 1,
    minHeight: 0,
  },
  singleButtonDesktop: { maxWidth: 400, alignSelf: 'center', minWidth: '50%' },
  conflictItemsDesktop: { paddingBottom: theme.spacing['6'] },
  conflictItemsMobile: { paddingBottom: theme.spacing['4'] },
  closeButton: { backgroundColor: theme.colors.APP_BACKGROUND },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  reorderConflictsHeadline: {
    defaultMessage: 'Oops, almost there.',
    description: 'Reorder | Conflicts | Headline',
  },
  reorderConflictsReorderingTo: {
    defaultMessage: 'Reordering to',
    description: 'Reorder | Conflicts | Reordering To',
  },
  reorderConflictsReorderingFrom: {
    defaultMessage: 'Reordering from',
    description: 'Reorder | Conflicts | Reordering From',
  },
  reorderConflictsNotice: {
    defaultMessage:
      'We have to make some changes to your order. Unavailable items will not be added to your bag.',
    description: 'Reorder | Conflicts | Notice',
  },
  reorderChangeLocation: {
    defaultMessage: 'Change location',
    description: 'Reorder | Change Location',
  },
  reorderContinue: {
    defaultMessage: 'Continue',
    description: 'Reorder | Continue',
  },
  reorderClearWarning: {
    defaultMessage: 'Choosing another restaurant will clear your current bag.',
    description: 'Reorder | Clear Warning',
  },
  reorderContinueToBag: {
    defaultMessage: 'Continue to bag',
    description: 'Reorder | Continue To Bag',
  },
});
